import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import { useAuth } from '../../hooks/useAuth';

export default function ForgotPassword() {
    const {
        handleForgotPassword,
        handleResetPassword,
        loading,
        error,
        successMessage
    } = useAuth();

    const [step, setStep] = useState(1); // Step 1: Email, Step 2: OTP and New Password
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Move to Step 2 when OTP is sent
    useEffect(() => {
        if (successMessage && step === 1) {
            setStep(2);
        }
    }, [successMessage, step]);

    // Step 1: Handle Email Submission
    const handleEmailSubmit = (event) => {
        event.preventDefault();
        handleForgotPassword(email);
    };

    // Step 2: Handle OTP and New Password Submission
    const handleResetSubmit = (event) => {
        event.preventDefault();

        if (newPassword !== confirmPassword) {
            alert("Passwords do not match. Please try again.");
            return;
        }

        handleResetPassword(email, otp, newPassword);
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                    <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
                    {step === 1 ? "Forgot Password" : "Reset Your Password"}
                </Typography>

                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 2,
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 16px'
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 2,
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 16px'
                        }}
                    >
                        {successMessage}
                    </Alert>
                )}

                {/* Step 1: Email Input */}
                {step === 1 && (
                    <Box component="form" onSubmit={handleEmailSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, py: 1.5, textTransform: 'none' }}
                            disabled={loading}
                        >
                            {loading ? "Sending OTP..." : "Send OTP"}
                        </Button>
                    </Box>
                )}

                {/* Step 2: OTP and New Password Input */}
                {step === 2 && (
                    <Box component="form" onSubmit={handleResetSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="otp"
                            label="One-Time Password (OTP)"
                            name="otp"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="newPassword"
                            label="New Password"
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm New Password"
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, py: 1.5, textTransform: 'none' }}
                            disabled={loading}
                        >
                            {loading ? "Resetting Password..." : "Reset Password"}
                        </Button>
                    </Box>
                )}

                <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Link href="/signin" variant="body2">
                        Back to Sign In
                    </Link>
                </Box>
            </Box>
        </Container>
    );
}
